// ==================== VALUE CALCULATORS ====================

export const VALUE_CALCULATORS = {
  ebitdaCalculator: {
    inputs: {
      annualRevenue: { label: 'Annual Revenue', type: 'number', required: true },
      annualCost: { label: 'Annual Operating Cost', type: 'number', required: true },
      currentEBITDA: { label: 'Current EBITDA', type: 'number', required: true },
      aiInvestment: { label: 'AI Investment', type: 'number', required: true },
      industry: { label: 'Industry', type: 'select', required: true },
      scale: { label: 'Scale', type: 'select', required: true }
    },
    calculate: (inputs, industryData) => {
      const multipliers = industryData.valueMultipliers;
      return {
        revenueUplift: inputs.annualRevenue * multipliers.revenue,
        costReduction: inputs.annualCost * multipliers.cost,
        incrementalEBITDA: (inputs.annualRevenue * multipliers.revenue) + (inputs.annualCost * multipliers.cost),
        newEBITDA: inputs.currentEBITDA + (inputs.annualRevenue * multipliers.revenue) + (inputs.annualCost * multipliers.cost),
        newEBITDAMargin: ((inputs.currentEBITDA + (inputs.annualRevenue * multipliers.revenue) + (inputs.annualCost * multipliers.cost)) / inputs.annualRevenue) * 100,
        roi: ((((inputs.annualRevenue * multipliers.revenue) + (inputs.annualCost * multipliers.cost)) / inputs.aiInvestment) * 100),
        paybackMonths: Math.round(inputs.aiInvestment / (((inputs.annualRevenue * multipliers.revenue) + (inputs.annualCost * multipliers.cost)) / 12))
      };
    }
  },
  
  assetUtilizationCalculator: {
    inputs: {
      totalAssets: { label: 'Total Asset Value', type: 'number', required: true },
      currentUtilization: { label: 'Current Utilization %', type: 'number', required: true },
      targetUtilization: { label: 'Target Utilization %', type: 'number', required: true }
    },
    calculate: (inputs) => {
      const improvement = inputs.targetUtilization - inputs.currentUtilization;
      const valueUnlocked = inputs.totalAssets * (improvement / 100);
      return {
        improvement,
        valueUnlocked,
        roi: (valueUnlocked / (inputs.totalAssets * 0.05)) * 100 // 5% investment
      };
    }
  },
  
  productivityCalculator: {
    inputs: {
      teamSize: { label: 'Team Size', type: 'number', required: true },
      averageSalary: { label: 'Average Salary', type: 'number', required: true },
      currentProductivity: { label: 'Current Productivity %', type: 'number', required: true },
      targetProductivity: { label: 'Target Productivity %', type: 'number', required: true }
    },
    calculate: (inputs) => {
      const improvement = inputs.targetProductivity - inputs.currentProductivity;
      const timeSaved = (improvement / 100) * inputs.teamSize * 2000; // hours
      const valueCreated = timeSaved * (inputs.averageSalary / 2000);
      return {
        improvement,
        timeSaved,
        valueCreated,
        headcountEquivalent: Math.round(timeSaved / 2000)
      };
    }
  },
  
  workingCapitalCalculator: {
    inputs: {
      inventory: { label: 'Current Inventory Value', type: 'number', required: true },
      receivables: { label: 'Current Receivables', type: 'number', required: true },
      payables: { label: 'Current Payables', type: 'number', required: true },
      industry: { label: 'Industry', type: 'select', required: true }
    },
    calculate: (inputs, industryData) => {
      const multipliers = industryData.valueMultipliers;
      const inventoryReduction = inputs.inventory * multipliers.workingCapital;
      const receivablesReduction = inputs.receivables * 0.20;
      const payablesImprovement = inputs.payables * 0.15;
      const totalCashRelease = inventoryReduction + receivablesReduction + payablesImprovement;
      
      return {
        inventoryReduction,
        receivablesReduction,
        payablesImprovement,
        totalCashRelease,
        costOfCapital: totalCashRelease * 0.10 // 10% cost of capital
      };
    }
  },
  
  npvCalculator: {
    calculate: (investment, annualCashFlow, years, discountRate = 0.10) => {
      let npv = -investment;
      for (let i = 1; i <= years; i++) {
        npv += annualCashFlow / Math.pow(1 + discountRate, i);
      }
      return {
        npv,
        irr: calculateIRR(investment, annualCashFlow, years),
        paybackPeriod: investment / annualCashFlow
      };
    }
  }
};

const calculateIRR = (investment, annualCashFlow, years) => {
  // Newton-Raphson method for IRR
  let rate = 0.1;
  for (let iteration = 0; iteration < 100; iteration++) {
    let npv = -investment;
    let derivative = 0;
    for (let i = 1; i <= years; i++) {
      npv += annualCashFlow / Math.pow(1 + rate, i);
      derivative -= i * annualCashFlow / Math.pow(1 + rate, i + 1);
    }
    const newRate = rate - npv / derivative;
    if (Math.abs(newRate - rate) < 0.0001) {
      return newRate * 100;
    }
    rate = newRate;
  }
  return rate * 100;
};

export const calculateTransformationValue = (architecture, requirements, industryData) => {
  const scale = requirements.scale || 'smb';
  
  // Base financial figures by scale
  const baseFigures = {
    startup: { revenue: 1000000, cost: 700000, ebitda: 300000, assets: 500000, teamSize: 10, inventory: 100000, receivables: 50000, payables: 30000 },
    smb: { revenue: 10000000, cost: 7000000, ebitda: 3000000, assets: 5000000, teamSize: 100, inventory: 1000000, receivables: 500000, payables: 300000 },
    enterprise: { revenue: 100000000, cost: 70000000, ebitda: 30000000, assets: 50000000, teamSize: 1000, inventory: 10000000, receivables: 5000000, payables: 3000000 }
  };
  
  const base = baseFigures[scale] || baseFigures.smb;
  const multipliers = industryData?.valueMultipliers || {
    revenue: 0.15, cost: 0.20, margin: 0.03, asset: 0.20,
    productivity: 0.30, throughput: 0.25, workingCapital: 0.15
  };
  
  const investment = architecture?.estimatedCost?.total?.high || architecture?.costModel?.total?.high || 500000;
  
  return {
    revenueUplift: base.revenue * multipliers.revenue,
    costReduction: base.cost * multipliers.cost,
    incrementalEBITDA: (base.revenue * multipliers.revenue) + (base.cost * multipliers.cost),
    assetValue: base.assets * multipliers.asset,
    productivityValue: base.teamSize * 100000 * multipliers.productivity,
    workingCapitalRelease: (base.inventory + base.receivables) * multipliers.workingCapital,
    totalAnnualValue: (base.revenue * multipliers.revenue) + (base.cost * multipliers.cost) + 
                      (base.assets * multipliers.asset) + (base.teamSize * 100000 * multipliers.productivity) +
                      ((base.inventory + base.receivables) * multipliers.workingCapital),
    investment,
    roi: ((((base.revenue * multipliers.revenue) + (base.cost * multipliers.cost)) / investment) * 100),
    paybackMonths: Math.round(investment / ((base.revenue * multipliers.revenue + base.cost * multipliers.cost) / 12)),
    npv: ((base.revenue * multipliers.revenue + base.cost * multipliers.cost) * 3) - investment
  };
};